import BenchlingConfig from 'BenchlingConfig';
import {getQueryParam} from 'BenchlingUtil';
import * as NavConstants from 'constants/NavConstants';
import EditorRoutes, {_editorBase} from 'foundation/EditorRoutes';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {makeNewDraftState} from 'llm/chat-library/ChatDraftState';
import ChatPrintView from 'llm/chat-library/export/ChatPrintView';
import {
  AISettingsTab,
  ChatSettingsTab,
  getAISidepanelState,
  InitialUI,
  openAgentFoundriesView,
  openChatSettingsModal,
  openRoutineHomepageView,
  openSettings,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {openChat, openDraftChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {
  getAgentTypeFromQueryStringValue,
  getAgentTypeText,
  getDefaultAgentType,
  isAgentTypeEnabled,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {ChatUseCaseLookupResult, findChatUseCaseById} from 'llm/chat-library/use-cases/chatUseCaseCatalog';
import {recordChatUseCasePrefill} from 'llm/chat-library/use-cases/recordChatUseCaseRun';
import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import {isMcpAiConnectorsFeatureEnabled} from 'llm/isMcpAiConnectorsFeatureEnabled';
import {
  buildChatSettingsModalState,
  CHAT_USE_CASE_QUERY_PARAM,
  getChatUseCaseCampaign,
  readAndClearAgentDataAccessHashFromLocation,
  stripChatUseCaseQueryParamFromLocation,
  UTM_CAMPAIGN_QUERY_PARAM,
} from 'llm/routes/UrlUtils';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import DatadogUtil from 'performance/DatadogUtil';
import {RouteBundle} from 'routing/RouteConstants';
import {info} from 'util/Logging';
import {renderReactComponent} from 'util/RootReactRender';

function getInitialUIFromQueryString(): InitialUI | undefined {
  const modalQueryValue = getQueryParam('modal');
  if (modalQueryValue == null) {
    return undefined;
  }
  const validValues = Object.values(InitialUI) as Array<string>;
  if (validValues.includes(modalQueryValue)) {
    return modalQueryValue as InitialUI;
  }
  return undefined;
}

/**
 * Parse the ?use-case query param and resolve it against the catalog.
 *
 * A use case hidden in this tenant is as unreachable as one that never existed. Both are logged so
 * a bad campaign link is queryable, but they do not toast: a malformed CTA in a widely sent email
 * would otherwise notify every recipient, and the empty composer is already the recovery. The
 * parameter is then dropped from the address bar so a refresh is an ordinary new chat rather than
 * another miss.
 */
function getChatUseCaseFromQueryString(queryParams: URLSearchParams): ChatUseCaseLookupResult | null {
  if (!queryParams.has(CHAT_USE_CASE_QUERY_PARAM)) {
    return null;
  }
  const chatUseCaseId = queryParams.get(CHAT_USE_CASE_QUERY_PARAM) ?? '';
  const lookupResult = findChatUseCaseById(chatUseCaseId);
  if (lookupResult == null) {
    info('[llm] Chat use case from URL is not available', {
      extra: {chatUseCaseId},
    });
    stripChatUseCaseQueryParamFromLocation();
    return null;
  }
  return lookupResult;
}

/**
 * Open a draft chat for the given agent, carrying the requested use case's prompt into the composer
 * and recording that prefill for campaign-funnel analytics.
 */
function openDraftChatForUseCase(
  agentType: AgentType,
  chatUseCaseLookup: ChatUseCaseLookupResult | null,
  initialUI: InitialUI | undefined,
  campaign: string | null
): void {
  const draftState =
    chatUseCaseLookup == null
      ? makeNewDraftState(agentType)
      : makeNewDraftState(agentType, chatUseCaseLookup.chatUseCase.prompt, {
          stagedChatUseCase: {
            chatUseCaseId: chatUseCaseLookup.chatUseCase.id,
            categoryId: chatUseCaseLookup.categoryId,
            campaign,
            source: 'url',
          },
        });
  openDraftChat(draftState, {initialUI});
  if (chatUseCaseLookup == null) {
    return;
  }

  const {chatUseCase, categoryId} = chatUseCaseLookup;
  recordChatUseCasePrefill(chatUseCase.id, {categoryId, campaign});
}

/**
 * Parse the ?agent and ?use-case query params and open a draft chat with the best available agent.
 *
 * An unavailable agent is fatal and an unavailable use case is not: without an agent there is no
 * chat to open, whereas a link that cannot offer its prompt can still drop the user into an empty
 * composer. Only the agent path toasts; a missing use case is logged and stripped instead.
 */
export function openDraftChatFromQueryString(): void {
  const queryParams = new URLSearchParams(window.location.search);
  const agentQueryValue = getQueryParam('agent');
  const initialUI = getInitialUIFromQueryString();
  const chatUseCaseLookup = getChatUseCaseFromQueryString(queryParams);
  const campaign = getChatUseCaseCampaign(queryParams.get(UTM_CAMPAIGN_QUERY_PARAM));

  if (agentQueryValue != null) {
    const requestedAgentType = getAgentTypeFromQueryStringValue(agentQueryValue);
    if (requestedAgentType == null) {
      notifications.warn('The agent is not available. Contact Benchling Support if this issue persists.');
    } else if (!isAgentTypeEnabled(requestedAgentType)) {
      notifications.warn(
        `You do not have access to ${getAgentTypeText(requestedAgentType)} Agent. Contact your administrator for access.`
      );
    } else {
      openDraftChatForUseCase(requestedAgentType, chatUseCaseLookup, initialUI, campaign);
    }
    return;
  }

  const defaultAgentType = getDefaultAgentType();
  if (defaultAgentType == null) {
    notifications.warn('You do not have access to chat. Contact your administrator.');
    return;
  }

  openDraftChatForUseCase(defaultAgentType, chatUseCaseLookup, initialUI, campaign);
}

/**
 * Open the chat settings modal from a URL. Mirrors the tab/selected item from the URL into global
 * client state (buildChatSettingsModalState owns per-tab defaulting, and preserves the current
 * group filter across a same-tab re-navigation since the group is not encoded in the URL).
 */
function openChatSettingsModalRoute(
  tab: ChatSettingsTab,
  selectedItemId: string | null,
  {createNew = false, agentDetailTab}: {createNew?: boolean; agentDetailTab?: 'data-access'} = {}
): void {
  if (tab === ChatSettingsTab.AGENTS && !isAgentsFeatureEnabled()) {
    notifications.error('Unable to open agent. Your account is not configured to access Agents.');
    return;
  }
  DatadogUtil.startRUMView('chat_settings');
  // Carry the current in-modal group filter across a same-tab re-navigation (selecting a row or
  // going Back only changes the URL's selectedItemId); buildChatSettingsModalState resets it to the
  // tab default only when landing on the tab fresh.
  const previous = getAISidepanelState().chatSettingsModal;
  const modalState = buildChatSettingsModalState(tab, selectedItemId, previous);
  const routeModalState =
    modalState.tab === ChatSettingsTab.AGENTS
      ? {
          ...modalState,
          ...(createNew ? {createNew: true} : {}),
          ...(agentDetailTab == null ? {} : {agentDetailTab}),
        }
      : modalState;
  _editorBase(() => {
    openChatSettingsModal(routeModalState);
    // The modal is only mounted inside the AI sidepanel, so ensure that section is open.
    SidepanelActions.openSection(NavConstants.SECTIONS.AI);
  });
}

/**
 * Open the Agent Foundries list in the sidepanel's main content slot. Shared by the canonical
 * `chats/agent-foundries` route and the `chats/agent-factories` backcompat route below.
 */
function openAgentFoundriesListRoute(): void {
  if (!BenchlingConfig.LLM_ENABLE_AGENT_FACTORIES) {
    return;
  }
  DatadogUtil.startRUMView('agent_foundries_list');
  _editorBase(() => {
    openAgentFoundriesView();
    SidepanelActions.expandSection(NavConstants.SECTIONS.AI);
  });
}

export const AIRoutes: RouteBundle = {
  domain: 'llm',
  routes: [
    // Only the tab and selected item are URL-driven; the per-tab list filter (group) is owned by
    // modal state, not the URL.
    {
      urlPattern: 'chats/settings/skills/:itemId',
      handler: (itemId: string): void => {
        openChatSettingsModalRoute(ChatSettingsTab.SKILLS, itemId);
      },
    },
    {
      urlPattern: 'chats/settings/skills',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.SKILLS, null);
      },
    },
    {
      urlPattern: 'chats/settings/agents/new',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.AGENTS, null, {createNew: true});
      },
    },
    {
      urlPattern: 'chats/settings/agents/:itemId',
      handler: (itemId: string): void => {
        openChatSettingsModalRoute(ChatSettingsTab.AGENTS, itemId, {
          agentDetailTab: readAndClearAgentDataAccessHashFromLocation(),
        });
      },
    },
    {
      urlPattern: 'chats/settings/agents',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.AGENTS, null);
      },
    },
    {
      urlPattern: 'chats/settings/connectors/:itemId',
      handler: (itemId: string): void => {
        if (!isMcpAiConnectorsFeatureEnabled()) {
          return;
        }
        openChatSettingsModalRoute(ChatSettingsTab.CONNECTORS, itemId);
      },
    },
    {
      urlPattern: 'chats/settings/connectors',
      handler: (): void => {
        if (!isMcpAiConnectorsFeatureEnabled()) {
          return;
        }
        openChatSettingsModalRoute(ChatSettingsTab.CONNECTORS, null);
      },
    },
    {
      urlPattern: 'chats/settings/usage',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.USAGE, null);
      },
    },
    {
      urlPattern: 'chats/settings/use-cases',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.USE_CASES, null);
      },
    },
    {
      urlPattern: 'chats/settings/general',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.GENERAL, null);
      },
    },
    {
      urlPattern: 'chats/settings',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.GENERAL, null);
      },
    },
    {
      urlPattern: 'ai-connectors',
      handler: (): void => {
        if (!isMcpAiConnectorsFeatureEnabled()) {
          return;
        }
        DatadogUtil.startRUMView('ai_connectors');
        _editorBase(() => {
          openSettings(AISettingsTab.AI_CONNECTORS);
          SidepanelActions.expandSection(NavConstants.SECTIONS.AI);
        });
      },
      legacy: true,
    },
    {
      urlPattern: 'chats/new',
      handler: (): void => {
        DatadogUtil.startRUMView('chats_new');
        _editorBase(() => {
          openDraftChatFromQueryString();
          SidepanelActions.expandSection(NavConstants.SECTIONS.AI);
        });
      },
    },
    // Agent Foundries list: rendered in the sidepanel's main content slot (the same slot the chat
    // occupies). Both entries must precede `chats/:id` below, or "agent-foundries"/"agent-factories"
    // would be swallowed as a chat id.
    {
      urlPattern: 'chats/agent-foundries',
      handler: openAgentFoundriesListRoute,
    },
    // Backcompat for a tab/link still on the pre-rename URL.
    {
      urlPattern: 'chats/agent-factories',
      handler: openAgentFoundriesListRoute,
    },
    // Agent (Routine) homepage: rendered in the sidepanel's main content slot (the same slot the chat
    // occupies).
    {
      urlPattern: 'chats/agents/:routineId',
      handler: (routineId: string): void => {
        if (!isAgentsFeatureEnabled()) {
          notifications.error('Unable to open agent. Your account is not configured to access Agents.');
          return;
        }
        DatadogUtil.startRUMView('routine_homepage');
        _editorBase(() => {
          openRoutineHomepageView(routineId);
          SidepanelActions.expandSection(NavConstants.SECTIONS.AI);
        });
      },
    },
    {
      urlPattern: 'chats/:chatSessionId/view',
      handler: (chatSessionId: string): void => {
        DatadogUtil.startRUMView('chat_export_view');
        renderReactComponent(ChatPrintView, {chatSessionId});
      },
    },
    {
      urlPattern: 'chats/:id',
      handler: (chatSessionId: string): void => {
        DatadogUtil.startRUMView('chats_existing');
        _editorBase(() => {
          openChat(chatSessionId);
        });
      },
    },
    {
      urlPattern: 'agent-foundries/:agentFoundryId',
      handler: EditorRoutes.agentFoundryEditor.bind(EditorRoutes),
      legacy: false,
    },
    // Backcompat for a tab/link still on the pre-rename URL.
    {
      urlPattern: 'agent-factories/:agentFoundryId',
      handler: EditorRoutes.agentFoundryEditor.bind(EditorRoutes),
      legacy: false,
    },
  ],
};
